import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Upload, Trash2, Users, Factory, Briefcase, AlertTriangle, X, FileUp, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

const TYPE_META = {
  customer: { label: "Müşteri", icon: Users, color: "#16a34a" },
  supplier: { label: "Tedarikçi", icon: Factory, color: "#dc2626" },
  employee: { label: "Personel", icon: Briefcase, color: "#2563eb" },
};

function Modal({ title, children, onClose, wide }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className={`modal ${wide ? "modal-wide" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}
function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

export default function CariPage({ company, companies, user }) {
  const canEdit = user?.role !== "viewer";
  const [cariList, setCariList] = useState([]);
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState("customer");
  const [showForm, setShowForm] = useState(false);
  const [showRecv, setShowRecv] = useState(null); // selected cari
  const [mikroStatus, setMikroStatus] = useState(null);
  const [importMsg, setImportMsg] = useState("");

  const load = async () => {
    const [c, d, m] = await Promise.all([
      axios.get(`${API}/cari`, { params: { cari_type: tab, company_id: company } }),
      axios.get(`${API}/cari-dashboard`),
      axios.get(`${API}/mikro/status`),
    ]);
    setCariList(c.data); setDash(d.data); setMikroStatus(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, company]);

  const importMikro = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setImportMsg("Yükleniyor...");
    try {
      const { data } = await axios.post(`${API}/cari/import-mikro`, form);
      setImportMsg(data.message); load();
    } catch (e) { setImportMsg(e.response?.data?.detail || "Hata"); }
  };
  const removeCari = async (c) => {
    if (!window.confirm(`${c.name} silinsin mi? Tüm alacak/borç kayıtları da silinecek.`)) return;
    await axios.delete(`${API}/cari/${c.id}`); load();
  };

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">CARİ HESAPLAR</p>
        <h2>Müşteri, Tedarikçi ve Personel</h2>
        <p className="muted">Alacak / borç bakiyeleri ve Mikro ERP entegrasyonu.</p>
      </div>
      {canEdit && <div className="toolbar-actions">
        <label className="outline-btn" data-testid="cari-import-mikro">
          <FileUp size={16} /> Mikro CSV aktar
          <input type="file" accept=".csv,.xlsx,.xls" onChange={importMikro} hidden data-testid="cari-import-input" />
        </label>
        <button className="primary-btn" data-testid="cari-new-btn" onClick={() => setShowForm(true)}>
          <Plus size={17} /> Yeni cari
        </button>
      </div>}
    </section>

    {importMsg && <div className="auth-err" style={{ marginBottom: 12 }} data-testid="cari-import-msg">{importMsg}</div>}

    {/* Mikro status banner */}
    {mikroStatus && <section className="panel" style={{ marginBottom: 16, padding: 14, display: "flex", alignItems: "center", gap: 14 }}>
      <RefreshCw size={18} color={mikroStatus.configured ? "#16a34a" : "#f59e0b"} />
      <div style={{ flex: 1 }}>
        <strong>Mikro ERP: {mikroStatus.configured ? "Bağlı" : "CSV modu"}</strong>
        <div className="muted" style={{ fontSize: 13 }}>{mikroStatus.message}</div>
      </div>
    </section>}

    {/* Summary cards */}
    {dash && <section className="metric-grid" data-testid="cari-dashboard">
      {Object.entries(TYPE_META).map(([k, meta]) => {
        const b = dash.buckets[k] || { open: 0, overdue: 0, count: 0 };
        const Icon = meta.icon;
        return <div key={k} className="metric-card" data-testid={`cari-metric-${k}`}>
          <div className="metric-head"><span className="metric-label"><Icon size={14} /> {meta.label}</span></div>
          <div className="metric-value">{money(b.open)}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {b.count} hesap · <span style={{ color: b.overdue > 0 ? "#dc2626" : "inherit" }}>{money(b.overdue)} gecikmiş</span>
          </div>
        </div>;
      })}
    </section>}

    {/* Tabs */}
    <section className="panel full-panel">
      <div className="tabs" style={{ display: "flex", gap: 8, padding: "12px 16px 0", borderBottom: "1px solid #e5e7eb" }}>
        {Object.entries(TYPE_META).map(([k, meta]) => (
          <button key={k} data-testid={`cari-tab-${k}`} onClick={() => setTab(k)}
            className={tab === k ? "tab-active" : "tab"}
            style={{
              padding: "8px 16px", border: "none", background: "none", cursor: "pointer",
              borderBottom: tab === k ? "2px solid #1e40af" : "2px solid transparent",
              fontWeight: tab === k ? 600 : 500, color: tab === k ? "#1e40af" : "#6b7280",
            }}>
            {meta.label} ({dash?.buckets[k]?.count || 0})
          </button>
        ))}
      </div>
      <div className="table-scroll"><table>
        <thead><tr>
          <th>AD / UNVAN</th><th>KOD</th><th>VERGİ NO</th><th>ŞİRKET</th>
          <th>AÇIK BAKİYE</th><th>GECİKMİŞ</th><th>İŞLEM</th>
        </tr></thead>
        <tbody>
          {cariList.length === 0 && <tr><td colSpan="7" style={{ textAlign: "center", padding: 20 }} className="muted">Henüz kayıt yok</td></tr>}
          {cariList.map(c => <tr key={c.id} data-testid={`cari-row-${c.id}`}>
            <td><strong>{c.name}</strong>{c.email && <span>{c.email}</span>}</td>
            <td>{c.code || "—"}</td>
            <td>{c.tax_no || "—"}</td>
            <td><span className="company-pill">{(c.company_id || "").toUpperCase()}</span></td>
            <td>{money(c.open_try || 0)}</td>
            <td style={{ color: (c.overdue_try || 0) > 0 ? "#dc2626" : "inherit" }}>
              {c.overdue_try > 0 && <AlertTriangle size={13} style={{ display: "inline", marginRight: 4 }} />}
              {money(c.overdue_try || 0)}
            </td>
            <td>
              <button className="outline-btn" data-testid={`cari-recv-${c.id}`} onClick={() => setShowRecv(c)} style={{ marginRight: 6 }}>Hareketler</button>
              {user?.role === "admin" && <button className="icon-btn" data-testid={`cari-del-${c.id}`} onClick={() => removeCari(c)}><Trash2 size={14} /></button>}
            </td>
          </tr>)}
        </tbody>
      </table></div>
    </section>

    {showForm && <CariForm companies={companies} defaultType={tab} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    {showRecv && <ReceivablesModal cari={showRecv} canEdit={canEdit} onClose={() => setShowRecv(null)} onChange={load} />}
  </div>;
}

function CariForm({ companies, defaultType, onClose, onDone }) {
  const [f, setF] = useState({ cari_type: defaultType, name: "", code: "", tax_no: "", phone: "", email: "", address: "", notes: "", insurance_policy: "", company_id: companies?.[0]?.id || "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/cari`, f); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || "Kayıt eklenemedi"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title="Yeni cari hesap">
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Tip"><select data-testid="cari-form-type" value={f.cari_type} onChange={e => setF({ ...f, cari_type: e.target.value })}>
          <option value="customer">Müşteri</option><option value="supplier">Tedarikçi</option><option value="employee">Personel</option>
        </select></Field>
        <Field label="Şirket"><select data-testid="cari-form-company" value={f.company_id} onChange={e => setF({ ...f, company_id: e.target.value })}>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select></Field>
      </div>
      <Field label="Ünvan / Ad Soyad"><input data-testid="cari-form-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required /></Field>
      <div className="row2">
        <Field label="Cari Kod (Mikro)"><input data-testid="cari-form-code" value={f.code} onChange={e => setF({ ...f, code: e.target.value })} /></Field>
        <Field label={f.cari_type === "employee" ? "T.C. Kimlik No" : "Vergi No"}><input data-testid="cari-form-taxno" value={f.tax_no} onChange={e => setF({ ...f, tax_no: e.target.value })} /></Field>
      </div>
      <div className="row2">
        <Field label="Telefon"><input data-testid="cari-form-phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="E-posta"><input data-testid="cari-form-email" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></Field>
      </div>
      {f.cari_type === "employee" && <Field label="Sigorta/Poliçe No"><input data-testid="cari-form-insurance" value={f.insurance_policy} onChange={e => setF({ ...f, insurance_policy: e.target.value })} /></Field>}
      <Field label="Adres"><input data-testid="cari-form-address" value={f.address} onChange={e => setF({ ...f, address: e.target.value })} /></Field>
      <Field label="Notlar"><textarea data-testid="cari-form-notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} /></Field>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button data-testid="cari-form-submit" className="primary-btn" disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</button>
      </div>
    </form>
  </Modal>;
}

function ReceivablesModal({ cari, canEdit, onClose, onChange }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/cari/${cari.id}/receivables`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const markPaid = async (r) => {
    await axios.patch(`${API}/receivables/${r.id}`, { status: "Tahsil edildi" });
    load(); onChange?.();
  };
  const del = async (r) => {
    if (!window.confirm("Silinsin mi?")) return;
    await axios.delete(`${API}/receivables/${r.id}`); load(); onChange?.();
  };
  const total = rows.reduce((s, r) => s + (r.try_amount || 0), 0);
  return <Modal onClose={onClose} wide title={`${cari.name} — Hareketler`}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0 14px" }}>
      <div className="muted">Toplam: <strong>{money(total)}</strong></div>
      {canEdit && <button className="primary-btn" data-testid="recv-new-btn" onClick={() => setShowForm(true)}><Plus size={15} /> Yeni hareket</button>}
    </div>
    <div className="table-scroll"><table>
      <thead><tr><th>TARİH</th><th>VADE</th><th>AÇIKLAMA</th><th>TUTAR</th><th>TL</th><th>DURUM</th><th>İŞLEM</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan="7" style={{ textAlign: "center", padding: 16 }} className="muted">Hareket yok</td></tr>}
        {rows.map(r => <tr key={r.id} data-testid={`recv-row-${r.id}`}>
          <td>{r.issue_date}</td>
          <td>{r.due_date}</td>
          <td><strong>{r.description}</strong>{r.invoice_no && <span>{r.invoice_no}</span>}</td>
          <td>{money(r.amount, r.currency)}</td>
          <td>{money(r.try_amount || 0)}</td>
          <td><span className={`type ${r.status === "Tahsil edildi" ? "income" : "expense"}`}>{r.status}</span></td>
          <td>
            {canEdit && r.status !== "Tahsil edildi" && <button className="outline-btn" onClick={() => markPaid(r)} data-testid={`recv-paid-${r.id}`}>Tahsil et</button>}
            {canEdit && <button className="icon-btn" onClick={() => del(r)} data-testid={`recv-del-${r.id}`}><Trash2 size={13} /></button>}
          </td>
        </tr>)}
      </tbody>
    </table></div>
    {showForm && <ReceivableForm cari={cari} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); onChange?.(); }} />}
  </Modal>;
}

function ReceivableForm({ cari, onClose, onDone }) {
  const [f, setF] = useState({
    cari_id: cari.id, direction: cari.cari_type === "supplier" ? "payable" : "receivable",
    invoice_no: "", description: "", amount: "", currency: "TRY", manual_rate: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    status: "Bekliyor", company_id: cari.company_id || "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const p = { ...f, amount: parseFloat(f.amount) };
      if (f.manual_rate) p.manual_rate = parseFloat(f.manual_rate); else delete p.manual_rate;
      await axios.post(`${API}/receivables`, p); onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title="Yeni hareket">
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Yön"><select data-testid="recv-direction" value={f.direction} onChange={e => setF({ ...f, direction: e.target.value })}>
          <option value="receivable">Alacak</option><option value="payable">Borç</option>
        </select></Field>
        <Field label="Fatura No"><input data-testid="recv-invoice-no" value={f.invoice_no} onChange={e => setF({ ...f, invoice_no: e.target.value })} /></Field>
      </div>
      <Field label="Açıklama"><input data-testid="recv-description" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} required /></Field>
      <div className="row2">
        <Field label="Tutar"><input data-testid="recv-amount" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} required /></Field>
        <Field label="Para birimi"><select data-testid="recv-currency" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>{["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}</select></Field>
      </div>
      {f.currency !== "TRY" && <Field label="Manuel kur (opsiyonel)"><input data-testid="recv-manual-rate" type="number" step="0.0001" value={f.manual_rate} onChange={e => setF({ ...f, manual_rate: e.target.value })} /></Field>}
      <div className="row2">
        <Field label="Düzenleme"><input data-testid="recv-issue-date" type="date" value={f.issue_date} onChange={e => setF({ ...f, issue_date: e.target.value })} required /></Field>
        <Field label="Vade"><input data-testid="recv-due-date" type="date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} required /></Field>
      </div>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button data-testid="recv-submit" className="primary-btn" disabled={busy}>{busy ? "..." : "Kaydet"}</button>
      </div>
    </form>
  </Modal>;
}
